// src/contexts/SubscriptionContext.tsx
import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { subscriptionApi, Subscription, UsageStats } from '../services/subscriptionApi';

interface SubscriptionContextType {
  subscription: Subscription | null;
  usageStats: UsageStats | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  showUpgradeModal: boolean;
  setShowUpgradeModal: (show: boolean) => void;
}

const defaultFreeSubscription: Subscription = {
  id: 'free',
  userId: 0,
  planType: 'free',
  status: 'active',
  paymentFrequency: null,
  currentPeriodStart: new Date().toISOString(),
  currentPeriodEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
  stripeSubscriptionId: null,
  stripeCustomerId: null,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  canceledAt: null,
  isActive: true,
  isCanceled: false,
  isPastDue: false,
  isTrialing: false,
  cancelAtPeriodEnd: false,
  cancelScheduledAt: null,
  trialEndsAt: null,
  trialDaysRemaining: 0,
  currentPeriodEndFormatted: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString(),
  daysUntilRenewal: 30,
  isExpiringSoon: false
};

const SubscriptionContext = createContext<SubscriptionContextType>({
  subscription: defaultFreeSubscription,
  usageStats: null,
  isLoading: false,
  error: null,
  refresh: async () => {},
  showUpgradeModal: false,
  setShowUpgradeModal: () => {},
});

interface SubscriptionProviderProps {
  children: ReactNode;
}

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const [subscription, setSubscription] = useState<Subscription | null>(defaultFreeSubscription);
  const [usageStats, setUsageStats] = useState<UsageStats | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Initial fetch of subscription data
  useEffect(() => {
    fetchSubscriptionData();
    
    // Set up a polling interval to refresh data every 5 minutes
    const interval = setInterval(() => {
      fetchSubscriptionData();
    }, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchSubscriptionData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Fetch subscription details
      try {
        const subscriptionData = await subscriptionApi.getSubscription();
        setSubscription(subscriptionData);
      } catch (error) {
        // If we get a 404 or any other error, treat it as a free plan
        console.log('No subscription found, defaulting to free plan');
        setSubscription(defaultFreeSubscription);
      }
      
      // Fetch usage statistics
      try {
        const usageData = await subscriptionApi.getCurrentUsage();
        setUsageStats(usageData);
      } catch (error) {
        console.log('Failed to fetch usage stats, defaulting to null');
        setUsageStats(null);
      }
    } catch (error) {
      console.error('Failed to fetch subscription data:', error);
      setError('Failed to load subscription data. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const refresh = async () => {
    await fetchSubscriptionData();
  };

  return (
    <SubscriptionContext.Provider
      value={{
        subscription,
        usageStats,
        isLoading,
        error,
        refresh,
        showUpgradeModal,
        setShowUpgradeModal,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => useContext(SubscriptionContext);

export default SubscriptionContext;